/**
 * Turnstile orchestrator: init, execute invisible widget, wait for API.
 * @package AdvancedTrackingManager
 */

import { deleteCookie, getCookie } from '@lib/cookies';
import { onReady, waitFor } from '@lib/dom';
import { createLogger } from '@lib/logger';
import { pushEvent } from '@lib/data-layer';
import { isFailTestSitekey, TEST_SITEKEYS } from './constants';
import { initStatus, updateStatus } from './status';
import { verifyTokenServerSide } from './verify';
import { loadTurnstileScript, showChallenge } from './challenge-ui';

const w = globalThis as unknown as Window;

const run = (): void => {
  if (typeof w.atmTurnstile === 'undefined') return;

  const config = w.atmTurnstile;
  const { siteKey, debugMode, testFlow, ajaxUrl } = config;

  const log = createLogger('ATM Turnstile', debugMode ?? false);

  initStatus(siteKey, debugMode);

  if (!siteKey) {
    log.debug('⚠️ Turnstile site key not configured - marking as not configured');
    updateStatus({ verified: false, pending: false, failed: false, token: null }, siteKey, debugMode ?? false);
    return;
  }

  const executeTurnstile = (): void => {
    const cookieStatus = getCookie('atm_turnstile_status');
    const turnstileChecked = sessionStorage.getItem('atm_turnstile_checked');

    if (cookieStatus) {
      try {
        const parsed = JSON.parse(cookieStatus) as Record<string, unknown>;
        if (parsed.sitekey && parsed.sitekey !== siteKey) {
          deleteCookie('atm_turnstile_status');
          sessionStorage.removeItem('atm_turnstile_checked');
          sessionStorage.removeItem('atm_turnstile_token');
        } else if (
          parsed.verified === true &&
          parsed.serverVerified === true &&
          !isFailTestSitekey(siteKey)
        ) {
          const token =
            sessionStorage.getItem('atm_turnstile_token') ?? (parsed.token as string) ?? null;
          updateStatus(
            { verified: true, serverVerified: true, pending: false, failed: false, token },
            siteKey,
            debugMode
          );
          log.debug('✅ Turnstile already verified (from cookie/sessionStorage)');
          return;
        } else if (parsed.failed === true || isFailTestSitekey(siteKey)) {
          updateStatus(
            { verified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          log.debug('❌ Turnstile previously failed or using fail test sitekey - not showing popup again');
          return;
        }
      } catch {
        log.debug('⚠️ Failed to parse cached status, proceeding with verification');
      }
    }

    if (turnstileChecked === 'true' && !isFailTestSitekey(siteKey)) {
      const token = sessionStorage.getItem('atm_turnstile_token');
      const serverVerified = sessionStorage.getItem('atm_turnstile_server_verified') === 'true';
      if (serverVerified && token) {
        updateStatus(
          { verified: true, serverVerified: true, pending: false, failed: false, token },
          siteKey,
          debugMode
        );
        log.debug('✅ Turnstile already verified (from sessionStorage, server verified)');
        return;
      }
      if (token) {
        verifyTokenServerSide(token, ajaxUrl ?? '')
          .then((result) => {
            if (result.success) {
              updateStatus(
                { verified: true, serverVerified: true, pending: false, failed: false, token },
                siteKey,
                debugMode ?? false
              );
            } else {
              updateStatus(
                { verified: false, serverVerified: false, pending: false, failed: true, token: null },
                siteKey,
                debugMode ?? false
              );
              showChallenge();
            }
          })
          .catch(() => {
            updateStatus(
              { verified: false, serverVerified: false, pending: false, failed: true, token: null },
              siteKey,
              debugMode ?? false
            );
            showChallenge();
          });
        return;
      }
    }

    const widgetContainer = document.createElement('div');
    widgetContainer.id = 'atm-turnstile-widget';
    widgetContainer.style.cssText = 'position: absolute; left: -9999px;';
    document.body.append(widgetContainer);

    const turnstileApi = w.turnstile as {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      ready?: (cb: () => void) => void;
    };
    const doRender = (): void => {
      turnstileApi.render(widgetContainer, {
      sitekey: siteKey,
      size: 'normal',
      theme: 'light',
      retry: 'never',
      callback: async (token: string) => {
        if (!token || typeof token !== 'string' || token.length < 10) {
          updateStatus(
            { verified: false, serverVerified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          showChallenge();
          return;
        }
        if (isFailTestSitekey(siteKey)) {
          updateStatus(
            { verified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          return;
        }
        updateStatus(
          { verified: true, serverVerified: false, pending: true, failed: false, token },
          siteKey,
          debugMode
        );
        const result = await verifyTokenServerSide(token, ajaxUrl);
        if (result.success) {
          updateStatus(
            { verified: true, serverVerified: true, pending: false, failed: false, token },
            siteKey,
            debugMode
          );
        } else {
          const codes = result.errorCodes?.length ? result.errorCodes.join(', ') : result.error;
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[ATM Turnstile] Server verification failed:', codes);
          }
          log.debug(
            '❌ Server verification failed - marking as failed',
            result.error,
            result.errorCodes ? `[${result.errorCodes.join(', ')}]` : ''
          );
          updateStatus(
            { verified: false, serverVerified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          showChallenge();
          return;
        }
        pushEvent('turnstile_verified', {
          turnstile_token: token,
          turnstile_method: 'invisible',
        });
        widgetContainer.remove();
      },
      'error-callback': () => {
        if (testFlow === 'failed') {
          updateStatus(
            { verified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          return;
        }
        if (testFlow === 'challenge_then_success') {
          widgetContainer.remove();
          showChallenge(TEST_SITEKEYS.PASS_VISIBLE);
          return;
        }
        if (testFlow === 'challenge_then_failed') {
          widgetContainer.remove();
          showChallenge(TEST_SITEKEYS.FAIL_VISIBLE);
          return;
        }
        if (isFailTestSitekey(siteKey)) {
          updateStatus(
            { verified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          return;
        }
        widgetContainer.remove();
        showChallenge();
      },
      'expired-callback': () => {
        sessionStorage.removeItem('atm_turnstile_checked');
        sessionStorage.removeItem('atm_turnstile_token');
        deleteCookie('atm_turnstile_status');
        updateStatus(
          { verified: false, pending: false, failed: false, token: null },
          siteKey,
          debugMode
        );
      },
      'timeout-callback': () => {
        if (testFlow === 'failed') {
          updateStatus(
            { verified: false, pending: false, failed: true, token: null },
            siteKey,
            debugMode
          );
          widgetContainer.remove();
          return;
        }
        if (testFlow === 'challenge_then_success') {
          widgetContainer.remove();
          showChallenge(TEST_SITEKEYS.PASS_VISIBLE);
          return;
        }
        if (testFlow === 'challenge_then_failed') {
          widgetContainer.remove();
          showChallenge(TEST_SITEKEYS.FAIL_VISIBLE);
          return;
        }
        widgetContainer.remove();
        showChallenge();
      },
    });
    };
    if (typeof turnstileApi.ready === 'function') {
      turnstileApi.ready(doRender);
    } else {
      doRender();
    }
  };

  const init = (): void => {
    if (typeof w.atmTurnstileStatus === 'undefined') {
      initStatus(siteKey, debugMode);
    }

    if (!testFlow && siteKey === TEST_SITEKEYS.INTERACTIVE) {
      loadTurnstileScript();
      waitFor(() => typeof w.turnstile !== 'undefined', { interval: 150, timeout: 12000 })
        .then(() => {
          showChallenge();
        })
        .catch(() => {
          log.debug('⚠️ Turnstile API not available after waiting - treating as success (interactive key)');
          updateStatus(
            { verified: true, serverVerified: true, pending: false, failed: false, token: null },
            siteKey,
            debugMode
          );
        });
      return;
    }

    loadTurnstileScript();
    waitFor(() => typeof w.turnstile !== 'undefined', { interval: 150, timeout: 12000 })
      .then(() => {
        executeTurnstile();
      })
      .catch(() => {
        log.debug('⚠️ Turnstile API not available after waiting - failing without showing challenge');
        updateStatus(
          { verified: false, serverVerified: false, pending: false, failed: true, token: null },
          siteKey,
          debugMode
        );
        // Do not show the "verify you are human" popup when Turnstile failed to load - the widget would not work.
      });
  };

  onReady(init);
};

run();
